/**
 * DG Predictive Search
 * Shopify Predictive Search API — /search/suggest.json
 * ThemeDGCore | Distribution Gauthier
 */
(function () {
    'use strict';

    const DEBOUNCE_MS = 260;
    const MAX_RESULTS = 6;
    const MIN_CHARS   = 2;
    const API_BASE    = '/search/suggest.json';

    /* ── Utilities ─────────────────────────────────────────────────── */
    function esc(str) {
        return String(str || '').replace(/[&<>"']/g, c =>
            ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
        );
    }

    function formatPrice(priceStr) {
        const val = parseFloat(priceStr);
        if (isNaN(val) || val === 0) return '';
        return val.toFixed(2).replace('.', ',') + '\u00a0$';
    }

    function debounce(fn, delay) {
        let timer;
        return function (...args) {
            clearTimeout(timer);
            timer = setTimeout(() => fn.apply(this, args), delay);
        };
    }

    function highlight(text, query) {
        if (!query) return esc(text);
        const re = new RegExp('(' + query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'gi');
        return esc(text).replace(re, '<mark class="dg-predict__hl">$1</mark>');
    }

    /* ── Per-input controller ──────────────────────────────────────── */
    function initPredict(input, dropdown) {
        // Abort controller scopes all listeners for this input instance
        var ac = new AbortController();
        var signal = ac.signal;
        let currentQuery = '';
        let activeIdx    = -1;
        let abortCtrl    = null;

        // ⚡ Move dropdown to <body> so position:fixed is relative to viewport,
        //    not to a potentially-transformed header stacking context
        document.body.appendChild(dropdown);

        /* ── Helpers ── */
        function getItems() {
            return dropdown.querySelectorAll('.dg-predict__item');
        }

        function setActive(idx) {
            const items = getItems();
            items.forEach(el => el.classList.remove('is-active'));
            activeIdx = Math.max(-1, Math.min(idx, items.length - 1));
            if (activeIdx >= 0) {
                items[activeIdx].classList.add('is-active');
                items[activeIdx].scrollIntoView({ block: 'nearest' });
            }
        }

        // Position dropdown directly aligned below the search input
        function positionDropdown() {
            const rect = input.getBoundingClientRect();
            dropdown.style.top   = `${rect.bottom + 4}px`;
            dropdown.style.left  = `${rect.left}px`;
            dropdown.style.width = `${rect.width}px`;
        }

        function close() {
            dropdown.hidden = true;
            dropdown.setAttribute('aria-hidden', 'true');
            activeIdx = -1;
        }

        function open() {
            positionDropdown();
            dropdown.hidden = false;
            dropdown.setAttribute('aria-hidden', 'false');
        }

        /* ── Render results ── */
        function render(products) {
            if (!products.length) {
                dropdown.hidden = true;
                return;
            }

            dropdown.innerHTML = products.map((p, i) => {
                const imgSrc  = p.featured_image?.url || p.image || '';
                const imgHtml = imgSrc
                    ? `<img src="${esc(imgSrc)}" alt="${esc(p.title)}" class="dg-predict__img" width="52" height="52" loading="lazy">`
                    : `<div class="dg-predict__img-placeholder"></div>`;

                const price = formatPrice(p.price);
                const avail = p.available === false
                    ? `<span class="dg-predict__badge">Épuisé</span>` : '';
                const type  = p.type
                    ? `<span class="dg-predict__type">${esc(p.type)}</span>` : '';

                return `<a href="${esc(p.url)}" class="dg-predict__item" role="option" data-idx="${i}" tabindex="-1">
                    <div class="dg-predict__img-wrap">${imgHtml}</div>
                    <div class="dg-predict__info">
                        <span class="dg-predict__title">${highlight(p.title, currentQuery)}</span>
                        ${type}
                    </div>
                    <div class="dg-predict__right">
                        ${price ? `<span class="dg-predict__price">${price}</span>` : ''}
                        ${avail}
                    </div>
                </a>`;
            }).join('');

            const seeAll = document.createElement('a');
            seeAll.href      = `/search?q=${encodeURIComponent(currentQuery)}&type=product`;
            seeAll.className = 'dg-predict__see-all';
            seeAll.setAttribute('tabindex', '-1');
            seeAll.innerHTML =
                `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>` +
                ` Voir tous les résultats pour <em>«\u00a0${esc(currentQuery)}\u00a0»</em>`;
            dropdown.appendChild(seeAll);

            open();
            setActive(-1);
        }

        /* ── Fetch suggestions ── */
        const fetchSuggest = debounce(async function (query) {
            if (!query || query.length < MIN_CHARS) {
                dropdown.hidden = true;
                return;
            }
            currentQuery = query;

            if (abortCtrl) abortCtrl.abort();
            abortCtrl = new AbortController();

            try {
                const url =
                    `${API_BASE}?q=${encodeURIComponent(query)}` +
                    `&resources[type]=product` +
                    `&resources[limit]=${MAX_RESULTS}` +
                    `&resources[options][unavailable_products]=last`;
                const res  = await fetch(url, { signal: abortCtrl.signal });
                if (!res.ok) return;
                const data = await res.json();
                const allProducts = data?.resources?.results?.products || [];
                const filteredProducts = allProducts.filter(p => !(p.url || '').includes('consigne-0-10'));
                render(filteredProducts);
            } catch (e) {
                if (e.name !== 'AbortError') dropdown.hidden = true;
            }
        }, DEBOUNCE_MS);

        /* ── Events ── */

        // Focus: re-trigger results if query exists
        //        Delay reposition so it fires AFTER the spring animation settles
        input.addEventListener('focus', function () {
            setTimeout(positionDropdown, 340);   // wait for 320ms burger collapse
            const q = input.value.trim();
            if (q.length >= MIN_CHARS) fetchSuggest(q);
        });

        // Typing
        input.addEventListener('input', function () {
            const q = this.value.trim();
            if (!q || q.length < MIN_CHARS) {
                dropdown.hidden = true;
                return;
            }
            fetchSuggest(q);
        });

        // Keyboard navigation
        input.addEventListener('keydown', function (e) {
            switch (e.key) {
                case 'ArrowDown':
                    if (!dropdown.hidden) {
                        e.preventDefault();
                        setActive(activeIdx + 1);
                    }
                    break;
                case 'ArrowUp':
                    if (!dropdown.hidden) {
                        e.preventDefault();
                        if (activeIdx <= 0) setActive(-1);
                        else setActive(activeIdx - 1);
                    }
                    break;
                case 'Enter': {
                    const items = getItems();
                    if (!dropdown.hidden && activeIdx >= 0 && items[activeIdx]) {
                        e.preventDefault();
                        window.location.href = items[activeIdx].href;
                    }
                    break;
                }
                case 'Escape':
                    close();
                    input.blur();
                    break;
            }
        });

        // Prevent blur before click registers on dropdown
        dropdown.addEventListener('mousedown', e => e.preventDefault());

        // Blur: close after short delay so item clicks register
        input.addEventListener('blur', function () {
            setTimeout(close, 200);
        });

        // Hover: highlight items
        dropdown.addEventListener('mousemove', function (e) {
            const item = e.target.closest('.dg-predict__item');
            if (item) {
                const idx = parseInt(item.dataset.idx, 10);
                if (!isNaN(idx)) setActive(idx);
            }
        });

        dropdown.addEventListener('mouseleave', () => setActive(-1));

        // Reposition on resize — scoped to this instance via AbortController
        window.addEventListener('resize', function () {
            if (!dropdown.hidden) positionDropdown();
        }, { signal: signal });

        // Extend close() to abort all listeners when the dropdown is destroyed
        var _originalClose = close;
        close = function () {
            _originalClose();
        };
    }

    /* ── Bootstrap ─────────────────────────────────────────────────── */
    // WeakSet tracks which inputs are already bound — prevents stacking listeners
    // if Shopify re-renders the header section in the theme editor.
    var _boundInputs = new WeakSet();

    function init() {
        document.querySelectorAll('[data-predict-target]').forEach(input => {
            if (_boundInputs.has(input)) return; // already initialized, skip
            const id       = input.dataset.predictTarget;
            const dropdown = document.getElementById(id);
            if (dropdown) {
                initPredict(input, dropdown);
                _boundInputs.add(input);
            }
        });
    }

    document.readyState === 'loading'
        ? document.addEventListener('DOMContentLoaded', init)
        : init();

    // Theme editor: re-scan for new inputs after a section re-render
    document.addEventListener('shopify:section:load', init);
})();
